import React from "react";
import { formatRate } from "../../utils/formatRate";
import { pricingTheme, type PricingTone } from "./pricingTheme";
import { CHARGE_LABEL, UNIT_SUFFIX, type PublicRateLine } from "./rateCardTypes";

export const formatCredits = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/0$/, ""));

interface HeaderProps {
    tone: PricingTone;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    title: string;
    subtitle: string;
    id?: string;
}

export function PricingSectionHeader({ tone, icon: Icon, title, subtitle, id }: HeaderProps) {
    const t = pricingTheme(tone);
    return (
        <div className="mb-6 flex items-start gap-3">
            <span className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${t.accentBg}`}>
                <Icon className="h-5 w-5" aria-hidden="true" />
            </span>
            <div>
                <h3 id={id} className={`text-xl font-bold ${t.heading}`}>
                    {title}
                </h3>
                <p className={`mt-1 text-sm ${t.body}`}>{subtitle}</p>
            </div>
        </div>
    );
}

export function RateAmount({ line, tone, large = false }: { line: PublicRateLine; tone: PricingTone; large?: boolean }) {
    const t = pricingTheme(tone);
    return (
        <span className="whitespace-nowrap">
            <span className={`${large ? "text-3xl font-extrabold" : "text-sm font-bold"} tabular-nums ${t.heading}`}>
                {formatRate(line.amountMinor, line.currency)}
            </span>
            <span className={`ml-1 ${large ? "text-base" : "text-xs"} ${t.muted}`}>{UNIT_SUFFIX[line.unit]}</span>
        </span>
    );
}

interface RateTableProps {
    tone: PricingTone;
    lines: PublicRateLine[];
    /** Labels each row by carrier rather than by charge, for per-provider tables. */
    labelBy?: "charge" | "carrier";
    caption: string;
}

export function RateTable({ tone, lines, labelBy = "charge", caption }: RateTableProps) {
    const t = pricingTheme(tone);
    return (
        <div className={`overflow-x-auto rounded-lg border ${t.divider}`}>
            <table className={`min-w-full divide-y ${t.divider}`}>
                <caption className="sr-only">{caption}</caption>
                <tbody className={`divide-y ${t.divider}`}>
                    {lines.map((line) => (
                        <tr key={`${line.chargeType}:${line.carrier ?? "all"}:${line.account ?? ""}`}>
                            <th scope="row" className={`px-4 py-3 text-left text-sm font-medium ${t.subheading}`}>
                                {labelBy === "carrier" ? line.carrierLabel ?? CHARGE_LABEL[line.chargeType] : CHARGE_LABEL[line.chargeType]}
                                {line.account === "own" && labelBy !== "carrier" && <span className={`ml-2 text-xs ${t.muted}`}>your own provider account</span>}
                            </th>
                            <td className="px-4 py-3 text-right">
                                <RateAmount line={line} tone={tone} />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
